if (!customElements.get('ai-chat-shell')) {
  class AiChatShell extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.requestId = globalThis.crypto?.randomUUID?.() || `chat-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      this.drawer = this.querySelector('[data-ai-chat-drawer]');
      this.contextBox = this.querySelector('[data-ai-chat-context]');
      this.launcher = this.querySelector('[data-ai-chat-open]');
      this.defaultContext = {
        department: this.form?.elements.department?.value || 'Sales',
        vehicle: this.form?.elements.vehicle?.value || '',
        stock: this.form?.elements.stock?.value || '',
        crm_key: this.form?.elements.crm_key?.value || '',
        product_id: this.form?.elements.product_id?.value || '',
        variant_id: this.form?.elements.variant_id?.value || '',
        message: ''
      };

      this.querySelectorAll('[data-ai-chat-open]').forEach((button) => button.addEventListener('click', () => this.open()));
      this.querySelectorAll('[data-ai-chat-close]').forEach((button) => button.addEventListener('click', () => this.close()));
      this.form?.addEventListener('submit', (event) => this.submit(event));
      const submitButton = this.form?.querySelector('[data-js-submit]');
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.removeAttribute('aria-disabled');
      }
      document.addEventListener('click', (event) => this.openFromContextLink(event));
      document.addEventListener('keydown', (event) => this.handleKeydown(event));
    }

    open(context = {}) {
      const resolvedContext = { ...this.defaultContext, ...context };
      this.previouslyFocused = document.activeElement;
      this.setAttribute('open', '');
      document.documentElement.classList.add('ai-chat-open');
      this.drawer.removeAttribute('inert');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.launcher?.setAttribute('aria-expanded', 'true');
      if (this.form.elements.department) this.form.elements.department.value = resolvedContext.department || 'Sales';
      if (this.form.elements.message) this.form.elements.message.value = resolvedContext.message || '';
      ['vehicle','stock','crm_key','product_id','variant_id'].forEach((key) => {
        if (this.form.elements[key]) this.form.elements[key].value = resolvedContext[key] || '';
      });
      const label = [resolvedContext.vehicle, resolvedContext.stock ? `Stock #${resolvedContext.stock}` : ''].filter(Boolean).join(' · ');
      if (label && this.contextBox) {
        this.contextBox.textContent = `Chatting about: ${label}`;
        this.contextBox.hidden = false;
      } else if (this.contextBox) {
        this.contextBox.textContent = '';
        this.contextBox.hidden = true;
      }
      setTimeout(() => this.form.elements.message?.focus(), 120);
    }

    close() {
      this.removeAttribute('open');
      document.documentElement.classList.remove('ai-chat-open');
      this.drawer.setAttribute('inert', '');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.launcher?.setAttribute('aria-expanded', 'false');
      if (this.previouslyFocused instanceof HTMLElement) this.previouslyFocused.focus();
    }

    handleKeydown(event) {
      if (!this.hasAttribute('open')) return;
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...this.drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }

    openFromContextLink(event) {
      let trigger = event.target.closest('[data-ai-chat-context-link]');
      const legacySmsLink = event.target.closest('a[href^="sms:"]:not([data-ai-direct-sms])');
      if (!trigger && legacySmsLink) trigger = legacySmsLink;
      if (!trigger) return;
      event.preventDefault();

      let legacyMessage = '';
      if (legacySmsLink) {
        const smsUrl = new URL(legacySmsLink.href);
        legacyMessage = decodeURIComponent(smsUrl.searchParams.get('body') || '').replace(/^\?&?body=/, '');
      }
      this.open({
        department: trigger.dataset.aiDepartment || 'Sales',
        vehicle: trigger.dataset.aiVehicle || this.defaultContext.vehicle,
        stock: trigger.dataset.aiStock || this.defaultContext.stock,
        crm_key: trigger.dataset.aiCrmKey || this.defaultContext.crm_key,
        product_id: trigger.dataset.aiProductId || this.defaultContext.product_id,
        variant_id: trigger.dataset.aiVariantId || this.defaultContext.variant_id,
        message: trigger.dataset.aiMessage || legacyMessage
      });
    }

    setStatus(message, state = '') {
      const status = this.querySelector('[data-ai-chat-status]');
      if (!status) return;
      status.textContent = message;
      status.dataset.state = state;
    }

    async submit(event) {
      event.preventDefault();
      const data = Object.fromEntries(new FormData(this.form).entries());
      const department = ['Sales', 'Parts', 'Service'].includes(data.department) ? data.department : 'Sales';
      const routingKey = department.toLowerCase();
      const isSales = department === 'Sales';
      const nameParts = String(data.name || '').trim().split(/\s+/).filter(Boolean);
      const payload = {
        ...data,
        department,
        routing_key: routingKey,
        workflow: isSales ? 'zoho_crm_lead' : 'department_request',
        module: isSales ? 'Leads' : '',
        lead_source: 'Website',
        lead_type: isSales ? 'Sales Website Message' : '',
        first_name: isSales ? (nameParts.shift() || '') : '',
        last_name: isSales ? (nameParts.join(' ') || 'Website Customer') : '',
        request_id: this.requestId,
        page_url: window.location.href,
        page_title: document.title,
        submitted_at: new Date().toISOString(),
        channel: 'website_message'
      };
      if (data.website) return;
      const button = this.form.querySelector('[type="submit"]');
      const recovery = this.querySelector('[data-ai-chat-recovery]');
      const original = button.textContent;
      button.disabled = true;
      button.textContent = 'Sending…';
      this.setStatus('');
      if (recovery) recovery.hidden = true;

      try {
        const endpoint = isSales ? this.dataset.salesEndpoint : this.dataset.departmentEndpoint;
        if (!endpoint) throw new Error(`${department} endpoint not configured`);
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 15000);
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(payload),
          signal: controller.signal
        });
        clearTimeout(timeout);
        if (!response.ok) throw new Error('Department request failed');
        this.form.reset();
        this.requestId = globalThis.crypto?.randomUUID?.() || `chat-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        this.setStatus(`Thanks — your message has been sent to our ${department.toLowerCase()} team.`, 'success');
      } catch (error) {
        console.error(error);
        if (recovery) {
          const smsBody = [
            'Hi Ignition Motorsports,',
            data.name ? `My name is ${data.name}.` : '',
            data.department ? `Department: ${data.department}.` : '',
            data.vehicle ? `Vehicle: ${data.vehicle}${data.stock ? ` (Stock #${data.stock})` : ''}.` : '',
            data.message || ''
          ].filter(Boolean).join('\n');
          recovery.href = `sms:+1${this.dataset.smsNumber || '2505108844'}?body=${encodeURIComponent(smsBody)}`;
          recovery.hidden = false;
        }
        this.setStatus('We could not send the message. Your request is ready to send as a normal text.', 'error');
      } finally {
        button.disabled = false;
        button.textContent = original;
      }
    }

    disconnectedCallback() {
      document.documentElement.classList.remove('ai-chat-open');
    }
  }
  customElements.define('ai-chat-shell', AiChatShell);
}
